import { motion, useReducedMotion } from 'motion/react';
import { useImageAspect, containedRectStyle } from './containedRect';

/** Default card-box aspect (w/h) the winner image is contained in — matches
 *  the `232 / 367` box in `WinnerContent` / `CardSlotTile`. */
const DEFAULT_BOX_ASPECT = 232 / 367;

export interface BorderCometProps {
  /** CSS colour expression for the comet — pass `glowColorFor(badge)` so the
   *  beam takes the card's rarity tint (and an operator theme can recolour it
   *  via the `--ui-badge-*` token inside that expression). */
  color: string;
  /** Card image URL — measured so the frame hugs the actual `object-contain`
   *  rectangle of the image, not the larger card box. Ignored when `aspect`
   *  is given. */
  src?: string | null;
  /** Known image aspect (w/h). Provided → skips measurement (and the
   *  one-frame ignite delay). Takes precedence over `src`. */
  aspect?: number;
  /** Aspect (w/h) of the box the image sits in. Default `232 / 367`. */
  boxAspect?: number;
  /** Frame corner radius (px number or any CSS length expression) — match
   *  the card's rounded-corner clip (`CARD_CORNER_RADIUS`). */
  radius?: number | string;
  /** Beam thickness in px — the painted band along the contour. */
  thickness?: number;
  /** Seconds per full lap. Mirrors the reference (`3.5s linear infinite`). */
  durationS?: number;
}

/**
 * A comet running along the contour of the card. A static rounded-rect frame
 * (its visible band carved out with a `mask-composite` ring), over which a
 * conic-gradient "comet head" sweeps once per `durationS` — so the bright
 * wedge reads as a single light racing around the edge, with a soft coloured
 * bloom trailing it (the `drop-shadow`, which hugs the masked comet's alpha).
 *
 * The frame is sized to the card's contained rectangle (see
 * {@link containedRectStyle}), so the beam tracks the card's real edges for
 * any image size — not the box. `radius` should match the card's corner clip.
 *
 * Rotation rides `motion/react` (`repeat: Infinity`, linear) to match the
 * reveal scene's other spinners (`RevealRays`). Honours
 * `prefers-reduced-motion`: when reduced, the comet is replaced by a calm,
 * static rarity-tinted frame. `pointer-events-none`, so it never blocks taps.
 */
export function BorderComet({
  color,
  src,
  aspect,
  boxAspect = DEFAULT_BOX_ASPECT,
  radius = 18,
  thickness = 2,
  durationS = 3.5,
}: BorderCometProps) {
  const reduce = useReducedMotion();
  const measured = useImageAspect(aspect == null ? src : null);
  const effectiveAspect = aspect ?? measured;

  // Nothing to frame until we know the card's true rectangle — the card
  // itself fades in, so the comet just "ignites" a frame later.
  if (effectiveAspect == null || effectiveAspect <= 0) return null;

  // Carve the painted area to a `thickness`-wide band hugging the frame:
  // full box XOR inner content-box = the border ring. Standard gradient-border
  // recipe (`-webkit-mask-composite: xor` ≡ `mask-composite: exclude`).
  const ringMask = {
    padding: thickness,
    borderRadius: radius,
    WebkitMask:
      'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
    WebkitMaskComposite: 'xor',
    mask: 'linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)',
    maskComposite: 'exclude',
  } as const;

  const framePos = containedRectStyle(effectiveAspect, boxAspect);

  if (reduce) {
    return (
      <div aria-hidden className="pointer-events-none absolute inset-0 z-20">
        <div
          style={{
            ...framePos,
            ...ringMask,
            background: `color-mix(in srgb, ${color} 55%, transparent)`,
          }}
        />
      </div>
    );
  }

  // The comet: a single bright wedge (full colour at 35°, fading to 40% alpha
  // on its shoulders, transparent everywhere else) — same shape as the
  // reference, tinted to the rarity colour instead of white.
  const comet = `conic-gradient(from 0deg,
    transparent 0deg,
    color-mix(in srgb, ${color} 40%, transparent) 25deg,
    ${color} 35deg,
    color-mix(in srgb, ${color} 40%, transparent) 45deg,
    transparent 60deg,
    transparent 360deg)`;

  return (
    <div aria-hidden className="pointer-events-none absolute inset-0 z-20">
      <div
        style={{
          ...framePos,
          ...ringMask,
          // Coloured bloom behind the beam. Applied after the mask, so the
          // shadow hugs the comet's swept arc rather than the whole box.
          filter: `drop-shadow(0 0 4px color-mix(in srgb, ${color} 70%, transparent)) drop-shadow(0 0 12px color-mix(in srgb, ${color} 45%, transparent))`,
        }}
      >
        {/* Oversized so the conic always covers the frame's corners at every
            angle (a rotated box would otherwise clip them). Only the rotation
            moves — the frame stays put. */}
        <motion.div
          className="absolute inset-[-50%]"
          style={{ background: comet, willChange: 'transform' }}
          initial={{ rotate: 0 }}
          animate={{ rotate: 360 }}
          transition={{ duration: durationS, ease: 'linear', repeat: Infinity }}
        />
      </div>
    </div>
  );
}
